import { create } from "zustand";

/**
 * Session marker in localStorage: it only tells the app "a refresh cookie may still be
 * valid, try a silent refresh on start". It is never a credential by itself.
 */
export const SESSION_MARKER_KEY = "pl.session";

export type AuthStatus = "unknown" | "authenticating" | "authenticated" | "anonymous";

interface AuthState {
  status: AuthStatus;
  /** Access token lives in memory only (never localStorage/sessionStorage). */
  accessToken: string | null;
  setStatus: (status: AuthStatus) => void;
  setAccessToken: (token: string | null) => void;
  /** Refresh token expired / revoked: drop the session and let the router bounce to /login. */
  expire: () => void;
}

function hasSessionMarker(): boolean {
  if (typeof window === "undefined") return false;
  return window.localStorage.getItem(SESSION_MARKER_KEY) === "1";
}

function writeSessionMarker(): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(SESSION_MARKER_KEY, "1");
}

function clearSessionMarker(): void {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(SESSION_MARKER_KEY);
}

export const useAuthStore = create<AuthState>((set) => ({
  status: hasSessionMarker() ? "unknown" : "anonymous",
  accessToken: null,
  setStatus: (status) => set({ status }),
  setAccessToken: (token) =>
    set(() => {
      if (token) {
        writeSessionMarker();
        return { accessToken: token, status: "authenticated" as AuthStatus };
      }
      // Clearing is explicit (logout / expiry) so we never downgrade by accident.
      return { accessToken: null };
    }),
  expire: () => {
    clearSessionMarker();
    set({ accessToken: null, status: "anonymous" });
  },
}));

export function hasStoredSession(): boolean {
  return hasSessionMarker();
}
